import { useCallback } from "react";
import { useSearchParams } from "react-router";

/**
 * Tiny URL-backed string picker state.
 *
 * Reads `key` off the URL search params; falls back to `defaultValue`.
 * Setter writes the new value (or clears the key if equal to default,
 * keeping URLs tidy for the common case). Updates always use
 * `replace: true` so picker tweaks don't accumulate back-button stops.
 *
 * Use one call per picker so each setter is independent and React
 * batches them naturally. Don't try to read multiple values via one
 * useSearchParams — the navigator pattern below relies on every setter
 * starting from the freshest URL state.
 */
export function useUrlPickerState(
  key: string,
  defaultValue: string,
): [string, (next: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(key) ?? defaultValue;
  const setValue = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === defaultValue || next === "") {
            params.delete(key);
          } else {
            params.set(key, next);
          }
          return params;
        },
        { replace: true },
      );
    },
    [key, defaultValue, setSearchParams],
  );
  return [value, setValue];
}
